/**
 * The worker, as this page holds it.
 *
 * CORE ships the client — `src/worker/client.ts` — and it owns the four things
 * that are easy to get wrong and expensive to get wrong twice: the reqId
 * ledger, the allocator seed across a terminate-and-respawn, the transfer
 * lists, and the version handshake. This module is the thin part that is
 * genuinely UI's: which URL, one instance per page, and what a user sees when
 * the handshake fails.
 *
 * My own `worker-client.ts` is gone. It was written against
 * `contracts/adapter.md` before CORE's engine landed and it was a second
 * implementation of the same protocol — which is the thing that goes wrong
 * quietly. Everything now types against `src/core/protocol.ts`, so a message
 * shape that drifts is a compile error on both sides of `postMessage`.
 *
 * ── THE CONSTRAINT THIS MODULE EXISTS TO ENFORCE ──────────────────────────
 * Documents live in the worker. CORE measured one `structuredClone` of a
 * 40,000-cue document at 1,230 ms coalesced and 2,532 ms with word runs, on a
 * desktop — four to eight times worse on a phone, against an interaction
 * budget of about that. A 60-cue window clones in 1.1 ms. So the main thread
 * holds `DocumentId`s and asks for windows, and nothing on this side ever
 * receives a document.
 */

import {
  SubtitleWorkerClient,
  WorkerCrash,
  WorkerFailure,
  WorkerVersionMismatch,
} from '../../../../../src/worker/client.ts';

export type { SubtitleWorkerClient };
export { WorkerCrash, WorkerFailure, WorkerVersionMismatch };

/**
 * Bundled by `build/build.mjs` from `src/worker/index.ts`.
 *
 * Cache-busted with the same build id the page's own assets carry. The URL is
 * assembled here rather than in a template because only JavaScript constructs
 * it, and an un-busted worker URL is the direct cause of the mismatch CORE's
 * handshake exists to refuse: an edge serving yesterday's `worker.js` to
 * today's page.
 */
function workerUrl(): string {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="build-id"]');
  const build = meta?.content.trim();
  return build
    ? `/static/build/worker.js?v=${encodeURIComponent(build)}`
    : '/static/build/worker.js';
}

let shared: SubtitleWorkerClient | null = null;

export interface WorkerHooks {
  onProgress?: (reqId: number, done: number, total: number, phase: string) => void;
  /** The worker died or was respawned. Everything it held is gone; re-issue it. */
  onRespawn?: (reason: 'terminate' | 'error') => void;
}

/**
 * One worker per tab. Not a pool: the work is CPU-bound and the box is the
 * user's phone as often as not.
 */
export function worker(hooks: WorkerHooks = {}): SubtitleWorkerClient {
  if (shared) return shared;
  shared = new SubtitleWorkerClient({
    url: workerUrl(),
    // Dev assertions cost a pass over the document at every pipeline boundary.
    devAssertions: false,
    ...(hooks.onProgress ? { onProgress: hooks.onProgress } : {}),
    ...(hooks.onRespawn ? { onRespawn: hooks.onRespawn } : {}),
    onDiagnostics: (diagnostics) => {
      // Diagnostics are the channel by which "line widths may be wrong for
      // CJK/Thai" reaches anyone at all — `measure()` returns a bare number.
      // Not user-facing, but never silently dropped either.
      for (const d of diagnostics) console.warn('[worker]', d);
    },
  });
  return shared;
}

/**
 * True when the failure is the version handshake refusing to run.
 *
 * CORE's client fails loudly here rather than producing documents the page
 * cannot read, and the instruction is not to work around it. A cached worker
 * against a fresh page is a real deployment state, and the honest thing to
 * show is that the page is out of date — not a parse error.
 */
export function isVersionMismatch(err: unknown): err is WorkerVersionMismatch {
  return err instanceof WorkerVersionMismatch;
}
